import {
  useCreateAvitoChannelChannelCreateAvitoPostMutation,
  useCreateMaxBotChannelChannelCreateMaxBotPostMutation,
  useCreateTelegramBotChannelChannelCreateTelegramBotPostMutation,
  useCreateVkBotChannelChannelCreateVkBotPostMutation,
  useCreateWidgetChannelChannelCreateWidgetPostMutation,
} from '@shared/api';
import type {
  ChannelAvitoFormValues,
  ChannelMaxFormValues,
  ChannelTelegramFormValues,
  ChannelVkFormValues,
  ChannelWidgetFormValues,
} from '../lib/types';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';

export const useCreateChannel = () => {
  const [createVkBotChannelMutation, { isLoading: isCreateVkBotChannelLoading }] =
    useCreateVkBotChannelChannelCreateVkBotPostMutation();
  const [createTelegramChannelMutation, { isLoading: isCreateAgentLoading }] =
    useCreateTelegramBotChannelChannelCreateTelegramBotPostMutation();
  const [createWidgetChannelMutation, { isLoading: isCreateWidgetChannelLoading }] =
    useCreateWidgetChannelChannelCreateWidgetPostMutation();
  const [createMaxBotChannelMutation, { isLoading: isCreateMaxBotChannelLoading }] =
    useCreateMaxBotChannelChannelCreateMaxBotPostMutation();
  const [createAvitoChannelMutation, { isLoading: isCreateAvitoChannelLoading }] =
    useCreateAvitoChannelChannelCreateAvitoPostMutation();

  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);

  const createChannelVkBot = async (values: ChannelVkFormValues) => {
    try {
      await createVkBotChannelMutation({
        organizationId: currentOrganization.id,
        createVkBotChannel: {
          name: values.name,
          agent_id: values.agentId,
          is_active: values.isActive,
          description: values.description,
          token: values.token,
        },
      }).unwrap();
    } catch {}
  };

  const createChannelWidget = async (values: ChannelWidgetFormValues) => {
    try {
      await createWidgetChannelMutation({
        organizationId: currentOrganization.id,
        createWidgetChannel: {
          name: values.name,
          agent_id: values.agentId,
          is_active: values.isActive,
          description: values.description,
          host: values.host,
          settings_id: values.presetId ?? null,
        },
      }).unwrap();
    } catch {}
  };

  const createTelegramChannel = async (values: ChannelTelegramFormValues) => {
    try {
      await createTelegramChannelMutation({
        organizationId: currentOrganization.id,
        createTelegramBotChannel: {
          name: values.name,
          agent_id: values.agentId,
          is_active: values.isActive,
          description: values.description,
          token: values.token,
          start_message: values.startMessage,
        },
      }).unwrap();
    } catch {}
  };

  const createMaxBotChannel = async (values: ChannelMaxFormValues) => {
    try {
      await createMaxBotChannelMutation({
        organizationId: currentOrganization.id,
        createMaxBotChannel: {
          name: values.name,
          agent_id: values.agentId,
          is_active: values.isActive,
          description: values.description,
          token: values.token,
          start_message: values.startMessage,
        },
      }).unwrap();
    } catch {}
  };

  const createAvitoChannel = async (values: ChannelAvitoFormValues) => {
    try {
      await createAvitoChannelMutation({
        organizationId: currentOrganization.id,
        createAvitoChannel: {
          name: values.name,
          agent_id: values.agentId,
          is_active: values.isActive,
          description: values.description,
          client_id: values.clientId,
          client_secret: values.clientSecret,
        },
      }).unwrap();
    } catch {}
  };

  return {
    createChannelVkBot,
    isCreateVkBotChannelLoading,
    createChannelWidget,
    isCreateWidgetChannelLoading,
    createTelegramChannel,
    isCreateAgentLoading,
    createMaxBotChannel,
    isCreateMaxBotChannelLoading,
    createAvitoChannel,
    isCreateAvitoChannelLoading,
  };
};
